import { ThemeProvider } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import React, { Suspense } from 'react';
import { BrowserRouter, useLocation, useRoutes } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseInit';
import { ColorModeContext, getDesignTokens } from './theme';
import { SubscriptionContextProvider, useAPI } from './StateProvider';
import { Provider, useDispatch } from 'react-redux';
import { setConfig } from './features/configs/configSilce';
import { setSavedImages } from './features/image/savedImageSlice';
import { upscaleReset } from './features/image/upscalingSlice';
import { fetchPersonalGeneratedImages } from './firestore/dataHandler';
import { store } from './store';

import Loader from './components/Loader';
import ScrollToTop from './components/utils/ScrollToTop';
// import SubscriptionUpgrade from "./components/SubscriptionUpgrage"
import mixpanel from 'mixpanel-browser';

const ExtensionArtGen = React.lazy(() =>
	import('./components/form/ExtensionArtGen'),
);
const SubscriptionUpgrade = React.lazy(() =>
	import('./components/SubscriptionUpgrage'),
);

const Stockimg = React.lazy(() => import('./components/stockimg'));
const StockimgForm = React.lazy(() =>
	import('./components/stockimg/StockimgForm'),
);
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const Login = React.lazy(() => import('./components/Login'));
const Signup = React.lazy(() => import('./components/Signup'));
const ForgetPassword = React.lazy(() => import('./components/ForgetPassword'));
const ProtectedRoute = React.lazy(() =>
	import('./components/protectedRoutes/ProtectedRoute'),
);
const Subscription = React.lazy(() => import('./components/Subscription'));
const AddRewards = React.lazy(() => import('./components/AddRewards'));
const Faq = React.lazy(() => import('./components/Faq'));
const HandleAlert = React.lazy(() =>
	import('./components/globalComponets/HandleAlert'),
);
const PricingNew = React.lazy(() => import('./components/PricingNew'));
const SerpImage = React.lazy(() => import('./components/SERP/SerpImage'));
const CreateCustomTemplate = React.lazy(() =>
	import('./components/customTemplate/CreateCustomTemplate'),
);
const DreamboothImageGeneration = React.lazy(() =>
	import('./components/dreambooth/DreamboothImageGeneration'),
);
const DreamBoothPresetPackages = React.lazy(() =>
	import('./components/dreambooth/DreamBoothPresetPackages'),
);
const DreamBoothTemplates = React.lazy(() =>
	import('./components/dreambooth/DreamBoothTemplates'),
);
const ImageGenerationLandingpageClone = React.lazy(() =>
	import('./components/ImageGenerationLandingpageClone'),
);

const GhibliArt = React.lazy(() => import('./components/GhibliArt'));
const GhibliStyle = React.lazy(() => import('./components/GhibliStyle'));

mixpanel.init('7eeb86efb67d69a2fdbd222ed96436c4', { debug: true });

// lazy loaded components

const Appsumo = React.lazy(() => import('./components/Appsumo'));
const Cancel = React.lazy(() => import('./components/Cancel'));
const ImageEditor = React.lazy(() => import('./components/imageEditor'));
const PaymentStatus = React.lazy(() => import('./components/PaymentStatus'));
const Pricing = React.lazy(() => import('./components/Pricing'));
const PrivacyAndPolicy = React.lazy(() =>
	import('./components/PrivacyAndPolicy'),
);
const Rewards = React.lazy(() => import('./components/Rewards'));
const TermsAndCondition = React.lazy(() =>
	import('./components/TermsAndCondition'),
);
const Verify = React.lazy(() => import('./components/Verify'));
const Temporary = React.lazy(() => import('./components/Temporary.js'));

const BulkImageGeneration = React.lazy(() =>
	import('./components/dreambooth/BulkImageGeneration'),
);
const AIProducts = React.lazy(() => import('./components/AIProducts'));
const ProductForm = React.lazy(() =>
	import('./components/AIProducts/ProductForm'),
);
const ProductsTemplates = React.lazy(() => './components/AIProducts/Templates');

const ProductTemplates = React.lazy(() =>
	import('./components/AIProducts/ProductTemplates'),
);
const PremadeProductImages = React.lazy(() =>
	import('./components/AIProducts/ProductTemplates/PremadeProductImages'),
);

const ProductImagesGeneration = React.lazy(() =>
	import('./components/AIProducts/ProductTemplates/ProductImagesGeneration'),
);
const Templates = React.lazy(() => import('./components/Templates'));
const Template = React.lazy(() => import('./components/Templates/Template'));
const Stocks = React.lazy(() => import('./components/Templates/Stock'));

const LandingPage = React.lazy(() => import('./components/LandingPage'));
const PremadeImages = React.lazy(() => import('./components/PremadeImages'));
const DreamboothImageGenerationTemplates = React.lazy(() =>
	import('./components/dreambooth/DreamboothImageGenerationTemplates'),
);

const AppWrapper = () => {
	const [user, loading] = useAuthState(auth);
	const [authenticated, setAuthenticated] = React.useState(false);

	const { data } = useAPI();
	let credits = data?.credits;
	// const subscription = data?.subscription;
	// console.log("DATA 1", data);

	const location = useLocation();
	const dispatch = useDispatch();

	React.useEffect(() => {
		if (!user) return;

		if (user.emailVerified) {
			setAuthenticated(true);
		}

		fetchPersonalGeneratedImages(user).then((res) => {
			// console.log("RESPONSE IS", res);
			// setGeneratedImage(res);
			let finalArray = [];
			res.forEach((each) => {
				let obj = {
					...each,
					src: `https://storage.googleapis.com/code-translate-c9640.appspot.com/generations/${each.id}`,
				};
				finalArray.push(obj);
			});
			// console.log("RES BEFORE DISPATCH", finalArray);
			dispatch(setSavedImages(finalArray));
			// get unique folder names.
			let folders = [...new Set(finalArray.map((item) => item.folderName))];
			// remove empty strings...
			folders = folders.filter((each) => each);
			// dispatch
			dispatch(
				setConfig({
					name: 'folders',
					value: folders,
				}),
			);
		});
	}, [user, dispatch]);

	React.useEffect(() => {
		// reset the upscaled image
		dispatch(upscaleReset());
	}, [location, dispatch]);

	const routes = useRoutes([
		{
			path: '/',
			element: (
				<Suspense fallback={<Loader />}>
					<LandingPage
						user={user}
						loading={loading}
						authenticated={authenticated}
						credits={credits}
						// subscription={subscription}
					/>
				</Suspense>
			),
		},
		// {
		//   path: "/dreambooth",
		//   element: (
		//     <ImageGenerationLandingpage
		//       user={user}
		//       loading={loading}
		//       authenticated={authenticated}
		//       credits={credits}
		//       // subscription={subscription}
		//     />
		//   ),
		// },
		{
			path: '/ai-avatars',
			element: (
				<Suspense fallback={<Loader />}>
					<ImageGenerationLandingpageClone
						user={user}
						loading={loading}
						authenticated={authenticated}
						credits={credits}
						// subscription={subscription}
					/>
				</Suspense>
			),
		},
		{
			path: '/premade-images',
			element: (
				<Suspense fallback={<Loader />}>
					<PremadeImages />
				</Suspense>
			),
		},
		{
			path: '/ai-avatars/imageGeneration/templates',
			element: (
				<Suspense fallback={<Loader />}>
					<DreamboothImageGenerationTemplates />
				</Suspense>
			),
		},
		{
			path: '/tools',
			element: (
				<Suspense fallback={<Loader />}>
					<StockimgForm user={user} />
					{/* <Stockimg /> */}
				</Suspense>
			),
		},
		// {
		//   path: "/stockimg/:id",
		//   element: (
		//     <Suspense fallback={<Loader />}>
		//       <StockimgForm />
		//     </Suspense>
		//   ),
		// },
		{
			path: '/dashboard',
			element: (
				<Suspense fallback={<Loader />}>
					<ProtectedRoute user={user} loading={loading}>
						<Dashboard
							user={user}
							loading={loading}
							authenticated={authenticated}
						/>
					</ProtectedRoute>
				</Suspense>
			),
		},
		{
			path: '/upgrade-subscription',
			element: (
				<Suspense fallback={<Loader />}>
					<SubscriptionUpgrade user={user} loading={loading} />
				</Suspense>
			),
		},

		{
			path: '/image/:id',
			element: (
				<Suspense fallback={<Loader />}>
					<ImageEditor />
				</Suspense>
			),
		},
		{
			path: '/templates',
			element: (
				<Suspense fallback={<Loader />}>
					<Templates user={user} />
				</Suspense>
			),
		},
		{
			path: '/templates/:id',
			element: (
				<Suspense fallback={<Loader />}>
					<Template />
				</Suspense>
			),
		},
		{
			path: '/stocks/:id',
			element: (
				<Suspense fallback={<Loader />}>
					<Stocks />
				</Suspense>
			),
		},
		{
			path: '/imageGeneration/templates',
			element: (
				<Suspense fallback={<Loader />}>
					<DreamBoothTemplates user={user} />
				</Suspense>
			),
		},
		{
			path: '/ai-avatars/imageGeneration',
			element: (
				<Suspense fallback={<Loader />}>
					<DreamboothImageGeneration />
				</Suspense>
			),
		},
		{
			path: '/ai-avatars/imageGeneration/bulk',
			element: (
				<Suspense fallback={<Loader />}>
					<BulkImageGeneration />
				</Suspense>
			),
		},

		// {
		//   path: "/imageGeneration/templates",
		//   element: <AllDreamBoothTemplates />,
		// },
		{
			path: '/imageGeneration/templates/:id',
			element: (
				<Suspense fallback={<Loader />}>
					<DreamBoothTemplates />
				</Suspense>
			),
		},
		{
			path: '/login',
			element: (
				<Suspense fallback={<Loader />}>
					<Login user={user} />
				</Suspense>
			),
		},
		{
			path: '/signup',
			element: (
				<Suspense fallback={<Loader />}>
					<Signup user={user} />
				</Suspense>
			),
		},
		{
			path: '/password',
			element: (
				<Suspense fallback={<Loader />}>
					<ForgetPassword user={user} />
				</Suspense>
			),
		},
		{
			path: '/privacy-and-policy',
			element: (
				<Suspense fallback={<Loader />}>
					<PrivacyAndPolicy user={user} />
				</Suspense>
			),
		},
		{
			path: '/terms',
			element: (
				<Suspense fallback={<Loader />}>
					<TermsAndCondition user={user} />
				</Suspense>
			),
		},
		{
			path: '/verify',
			element: (
				<Suspense fallback={<Loader />}>
					<Verify user={user} />
				</Suspense>
			),
		},
		{
			path: '/pricing_old',
			element: (
				<Suspense fallback={<Loader />}>
					<Pricing user={user} />
				</Suspense>
			),
		},
		{
			path: '/pricing',
			element: (
				<Suspense fallback={<Loader />}>
					<PricingNew user={user} />
				</Suspense>
			),
		},
		{
			path: '/code',
			element: (
				<Suspense fallback={<Loader />}>
					<Appsumo user={user} />
				</Suspense>
			),
		},
		{
			path: '/rewards',
			element: (
				<Suspense fallback={<Loader />}>
					<Rewards user={user} />
				</Suspense>
			),
		},
		{
			path: '/addrewards',
			element: (
				<Suspense fallback={<Loader />}>
					<AddRewards user={user} />
				</Suspense>
			),
		},
		{
			path: '/payment',
			element: (
				<Suspense fallback={<Loader />}>
					<PaymentStatus />
				</Suspense>
			),
		},
		{
			path: '/cancel',
			element: (
				<Suspense fallback={<Loader />}>
					<Cancel user={user} />
				</Suspense>
			),
		},
		{
			path: '/subscription',
			element: (
				<Suspense fallback={<Loader />}>
					<Subscription user={user} />
				</Suspense>
			),
		},
		{
			path: '/faq',
			element: (
				<Suspense fallback={<Loader />}>
					<Faq />
				</Suspense>
			),
		},
		{
			path: '/serpimage',
			element: (
				<Suspense fallback={<Loader />}>
					<SerpImage />
				</Suspense>
			),
		},
		// {
		//   path: "/serpEditor",
		//   element: <SerpEditor />,
		// },
		{
			path: '/test',
			element: (
				<Suspense fallback={<Loader />}>
					<Temporary />
				</Suspense>
			),
		},
		{
			path: '/customTemplate',
			element: (
				<Suspense fallback={<Loader />}>
					<CreateCustomTemplate user={user} credits={credits} />
				</Suspense>
			),
		},
		{
			path: '/presetPackages',
			element: (
				<Suspense fallback={<Loader />}>
					<DreamBoothPresetPackages user={user} />
				</Suspense>
			),
		},
		{
			path: '/presetPackages/bulk',
			element: (
				<Suspense fallback={<Loader />}>
					<DreamBoothPresetPackages bulk={true} user={user} />
				</Suspense>
			),
		},
		{
			path: '*',
			element: <LandingPage />,
		},

		//ai- products
		{
			path: '/ai-products',
			element: (
				<Suspense fallback={<Loader />}>
					<AIProducts />
				</Suspense>
			),
		},
		{
			path: '/ai-products/generate-products',
			element: (
				<Suspense fallback={<Loader />}>
					<ProductForm />
				</Suspense>
			),
		},
		{
			path: '/ai-products/templates',
			element: (
				<Suspense fallback={<Loader />}>
					<ProductTemplates />
				</Suspense>
			),
		},
		{
			path: '/ai-products/premade-products-images',
			element: (
				<Suspense fallback={<Loader />}>
					<PremadeProductImages />
				</Suspense>
			),
		},
		{
			path: '/ai-products/products-images-generation',
			element: (
				<Suspense fallback={<Loader />}>
					<ProductImagesGeneration user={user} />
				</Suspense>
			),
		},
		{
			path: '/extension',
			element: (
				<Suspense fallback={<Loader />}>
					<ExtensionArtGen user={user} />
				</Suspense>
			),
		},
		{
			path: '/products/ghibli-art',
			element: (
				<Suspense fallback={<Loader />}>
					<GhibliArt />
				</Suspense>
			),
		},
		{
			path: '/tools/ghibli-style',
			element: (
				<Suspense fallback={<Loader />}>
					<GhibliStyle />
				</Suspense>
			),
		},
	]);

	return routes;
};

// const theme = createTheme({
//     typography: {
//         fontFamily: ["Inter", "cursive"].join(","),
//     },
//     overrides: {
//         MuiSelect: {
//             select: {
//                 padding: "unset !important",
//                 //padding: "4px 0px 10px 130px !important"
//             },
//         },
//     },
// });

export default function App() {
	const [mode, setMode] = React.useState('light');

	React.useEffect(() => {
		if (localStorage.getItem('mode')) {
			setMode(localStorage.getItem('mode'));
		} else {
			localStorage.setItem('mode', 'light');
		}
	}, []);

	const colorMode = React.useMemo(
		() => ({
			toggleColorMode: () => {
				let prev = localStorage.getItem('mode');
				setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
				localStorage.setItem('mode', prev === 'light' ? 'dark' : 'light');
			},
		}),
		[],
	);

	const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);
	console.log(mode, 'MODE');

	// React.useEffect(()=>{

	// })

	return (
		<ColorModeContext.Provider value={colorMode}>
			<ThemeProvider theme={theme}>
				<SubscriptionContextProvider>
					<Provider store={store}>
						<BrowserRouter>
							<AppWrapper />
							<ScrollToTop />
							<HandleAlert />
						</BrowserRouter>
					</Provider>
				</SubscriptionContextProvider>
			</ThemeProvider>
		</ColorModeContext.Provider>
	);
}
